import * as React from 'react'
import {Navigate} from 'react-router'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faTrash} from '@fortawesome/free-solid-svg-icons'
import {Button} from '@frontrow/console/src/components/button'
import {exhaustive} from '@freckle/exhaustive'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'

import {type DetailedSchoolT} from '@frontrow/console/src/models/school'
import {
  SchoolsDeleteModal,
  type State as DeleteModalState
} from '@frontrow/console/src/components/pages/schools-page/schools-delete-modal'
import {
  useSchoolDeletionInfo,
  useDeleteSchool,
  useMergeSchool,
  useSchoolsByDistrict
} from '@frontrow/console/src/hooks/use-school'
import {useModalForm} from '@frontrow/console/src/hooks/use-modal-form'

type Props = {
  schoolToDelete: DetailedSchoolT
}

export function SchoolsDeleteButtonModal(props: Props): React.ReactElement {
  const {schoolToDelete} = props
  const {id: schoolId, districtId} = schoolToDelete
  const {isOpen: modalVisible, open, close: hideModal} = useModalForm()
  const [editSchoolId, setEditSchoolId] = React.useState<number | undefined | null>(null)

  const {schools: schoolsInDistrict} = useSchoolsByDistrict(districtId)

  const {schoolDeletionInfo, isLoading: isDeletionInfoLoading} = useSchoolDeletionInfo(
    schoolId,
    modalVisible
  )
  const {deleteSchool, error: deleteError} = useDeleteSchool()
  const {mergeSchool} = useMergeSchool()

  const submitForm = async (state: DeleteModalState) => {
    switch (state.tag) {
      case 'safe-delete':
        await deleteSchool(schoolId)
        break
      case 'needs-merge':
        const sourceSchoolId = schoolId
        const targetSchoolId = state.selectedTargetSchoolId
        let continueDelete
        try {
          await mergeSchool(sourceSchoolId, targetSchoolId)
          continueDelete = true
        } catch {
          continueDelete = false
        }
        if (continueDelete) {
          await deleteSchool(schoolId)
        }
        break
      case 'cannot-delete':
        switch (state.reason) {
          // @ts-ignore Potential error with the code
          case 'no-data':
            break
          // @ts-ignore Potential error with the code
          case 'idp-managed':
            setEditSchoolId(schoolId)
            break
          case 'active-crm':
            break
          case 'cannot-merge':
            break
          // @ts-ignore Potential error with the code
          case 'star-enabled':
            setEditSchoolId(schoolId)
            break
          default:
            exhaustive(state.reason)
        }
        break
      default:
        exhaustive(state as never)
    }
    hideModal()
  }

  if (editSchoolId !== null && editSchoolId !== undefined) {
    return <Navigate to={`/schools/${editSchoolId}/edit`} />
  }

  const modal =
    modalVisible &&
    (isDeletionInfoLoading || !schoolDeletionInfo ? (
      <SpinnerOverlay />
    ) : (
      <SchoolsDeleteModal
        show={modalVisible}
        hideModal={hideModal}
        schoolToDelete={schoolToDelete}
        schoolsInDistrict={schoolsInDistrict}
        onSubmit={submitForm}
        schoolDeletionInfo={schoolDeletionInfo}
      />
    ))

  return (
    <React.Fragment>
      {deleteError && <ErrorAlert>{deleteError}</ErrorAlert>}
      <Button title="Delete School" style="link" onClick={open}>
        <FontAwesomeIcon icon={faTrash} size="lg" />
      </Button>
      {modal}
    </React.Fragment>
  )
}
